import MetricWidget from './widgets/MetricWidget';
import ChartWidget from './widgets/ChartWidget';
import TableWidget from './widgets/TableWidget';
import GaugeWidget from './widgets/GaugeWidget';
import StatusListWidget from './widgets/StatusListWidget';
import ProgressWidget from './widgets/ProgressWidget';
import ExtraWidget from './widgets/ExtraWidget';

const widgetRegistry = {
    'voltage-gauge': {
        type: 'voltage-gauge',
        label: 'Voltage Gauge',
        icon: 'fas fa-bolt',
        description: 'Line-to-neutral / line-to-line voltage',
        component: GaugeWidget,
        variantFamily: 'gauge',
        defaultVariant: 'speedometer',
        defaultTitle: 'Voltage (V)',
        defaultLayout: { w: 2, h: 3, minW: 2, minH: 3 },
        defaultSettings: { max: 480, unit: 'V', value: 0 },
        defaultDataSource: { endpoint: '' },
    },
    'current-gauge': {
        type: 'current-gauge',
        label: 'Current Gauge',
        icon: 'fas fa-wave-square',
        description: 'Average phase current',
        component: GaugeWidget,
        variantFamily: 'gauge',
        defaultVariant: 'arc',
        defaultTitle: 'Current (A)',
        defaultLayout: { w: 2, h: 3, minW: 2, minH: 3 },
        defaultSettings: { max: 50, unit: 'A', value: 0 },
        defaultDataSource: { endpoint: '' },
    },
    'power-gauge': {
        type: 'power-gauge',
        label: 'Power (kW) Gauge',
        icon: 'fas fa-plug',
        description: 'Real power draw',
        component: GaugeWidget,
        variantFamily: 'gauge',
        defaultVariant: 'radial-full',
        defaultTitle: 'Power (kW)',
        defaultLayout: { w: 2, h: 3, minW: 2, minH: 3 },
        defaultSettings: { max: 10000, unit: 'kW', value: 0 },
        defaultDataSource: { endpoint: '' },
    },
    'kva-gauge': {
        type: 'kva-gauge',
        label: 'Apparent Power (kVA)',
        icon: 'fas fa-charging-station',
        description: 'Apparent power (kVA)',
        component: GaugeWidget,
        variantFamily: 'gauge',
        defaultVariant: 'ring',
        defaultTitle: 'KVA',
        defaultLayout: { w: 2, h: 3, minW: 2, minH: 3 },
        defaultSettings: { max: 10000, unit: 'kVA', value: 0 },
        defaultDataSource: { endpoint: '' },
    },
    'frequency-gauge': {
        type: 'frequency-gauge',
        label: 'Frequency Gauge',
        icon: 'fas fa-broadcast-tower',
        description: 'Grid frequency',
        component: GaugeWidget,
        variantFamily: 'gauge',
        defaultVariant: 'bar-horizontal',
        defaultTitle: 'Frequency (Hz)',
        defaultLayout: { w: 2, h: 3, minW: 2, minH: 3 },
        defaultSettings: { max: 80, unit: 'Hz', value: 0 },
        defaultDataSource: { endpoint: '' },
    },
    'pf-gauge': {
        type: 'pf-gauge',
        label: 'Power Factor Gauge',
        icon: 'fas fa-percentage',
        description: 'Average power factor',
        component: GaugeWidget,
        variantFamily: 'gauge',
        defaultVariant: 'badge',
        defaultTitle: 'Power Factor',
        defaultLayout: { w: 2, h: 3, minW: 2, minH: 3 },
        defaultSettings: { max: 1, unit: 'PF', value: 0 },
        defaultDataSource: { endpoint: '' },
    },
    'temperature-gauge': {
        type: 'temperature-gauge',
        label: 'Temperature Gauge',
        icon: 'fas fa-thermometer-half',
        description: 'Ambient / panel temperature',
        component: GaugeWidget,
        variantFamily: 'gauge',
        defaultVariant: 'bar-vertical',
        defaultTitle: 'Temperature (°C)',
        defaultLayout: { w: 2, h: 3, minW: 2, minH: 3 },
        defaultSettings: { max: 100, unit: '°C', value: 0 },
        defaultDataSource: { endpoint: '' },
    },
    'humidity-gauge': {
        type: 'humidity-gauge',
        label: 'Humidity Gauge',
        icon: 'fas fa-tint',
        description: 'Relative humidity',
        component: GaugeWidget,
        variantFamily: 'gauge',
        defaultVariant: 'radial-half',
        defaultTitle: 'Humidity (%)',
        defaultLayout: { w: 2, h: 3, minW: 2, minH: 3 },
        defaultSettings: { max: 100, unit: '%', value: 0 },
        defaultDataSource: { endpoint: '' },
    },
    'co2-gauge': {
        type: 'co2-gauge',
        label: 'CO₂ Level Gauge',
        icon: 'fas fa-smog',
        description: 'Indoor air CO₂ concentration',
        component: GaugeWidget,
        variantFamily: 'gauge',
        defaultVariant: 'digital',
        defaultTitle: 'CO₂ (ppm)',
        defaultLayout: { w: 2, h: 3, minW: 2, minH: 3 },
        defaultSettings: { max: 2000, unit: 'ppm', value: 0 },
        defaultDataSource: { endpoint: '' },
    },
    'runtime-gauge': {
        type: 'runtime-gauge',
        label: 'Equipment Runtime Gauge',
        icon: 'fas fa-stopwatch',
        description: 'Runtime hours vs. service interval',
        component: GaugeWidget,
        variantFamily: 'gauge',
        defaultVariant: 'led',
        defaultTitle: 'Runtime (%)',
        defaultLayout: { w: 2, h: 3, minW: 2, minH: 3 },
        defaultSettings: { max: 100, unit: '%', value: 0 },
        defaultDataSource: { endpoint: '' },
    },
    'trend-chart': {
        type: 'trend-chart',
        label: 'Energy Trend Chart',
        icon: 'fas fa-chart-line',
        description: 'Consumption over time, e.g. hourly/daily kWh',
        component: ChartWidget,
        variantFamily: 'chart',
        defaultVariant: 'line',
        defaultTitle: 'Hourly Consumption',
        defaultLayout: { w: 6, h: 4, minW: 4, minH: 3 },
        defaultSettings: { color: '#4318FF' },
        defaultDataSource: { endpoint: '' },
    },
    'thd-chart': {
        type: 'thd-chart',
        label: 'THD Chart',
        icon: 'fas fa-signal',
        description: 'Total harmonic distortion by phase',
        component: ChartWidget,
        variantFamily: 'chart',
        defaultVariant: 'radar',
        defaultTitle: 'Voltage THD',
        defaultLayout: { w: 6, h: 4, minW: 4, minH: 3 },
        defaultSettings: { color: '#00B5D8' },
        defaultDataSource: { endpoint: '' },
    },
    'cost-chart': {
        type: 'cost-chart',
        label: 'Energy Cost Chart',
        icon: 'fas fa-dollar-sign',
        description: 'Cost trend, e.g. daily/monthly spend',
        component: ChartWidget,
        variantFamily: 'chart',
        defaultVariant: 'area',
        defaultTitle: 'Energy Cost',
        defaultLayout: { w: 6, h: 4, minW: 4, minH: 3 },
        defaultSettings: { color: '#22C55E' },
        defaultDataSource: { endpoint: '' },
    },
    'demand-chart': {
        type: 'demand-chart',
        label: 'Peak Demand Chart',
        icon: 'fas fa-gauge-high',
        description: 'Peak kW demand over time',
        component: ChartWidget,
        variantFamily: 'chart',
        defaultVariant: 'column',
        defaultTitle: 'Peak Demand (kW)',
        defaultLayout: { w: 6, h: 4, minW: 4, minH: 3 },
        defaultSettings: { color: '#f87171' },
        defaultDataSource: { endpoint: '' },
    },
    'pf-chart': {
        type: 'pf-chart',
        label: 'Power Factor Trend',
        icon: 'fas fa-braille',
        description: 'Power factor over time, by phase',
        component: ChartWidget,
        variantFamily: 'chart',
        defaultVariant: 'radialBar',
        defaultTitle: 'Power Factor Trend',
        defaultLayout: { w: 6, h: 4, minW: 4, minH: 3 },
        defaultSettings: { color: '#fbbf24' },
        defaultDataSource: { endpoint: '' },
    },
    'load-profile-chart': {
        type: 'load-profile-chart',
        label: 'Load Profile Chart',
        icon: 'fas fa-th',
        description: '24-hour load profile breakdown',
        component: ChartWidget,
        variantFamily: 'chart',
        defaultVariant: 'heatmap',
        defaultTitle: 'Load Profile (24h)',
        defaultLayout: { w: 6, h: 4, minW: 4, minH: 3 },
        defaultSettings: { color: '#4318FF' },
        defaultDataSource: { endpoint: '' },
    },
    'diesel-chart': {
        type: 'diesel-chart',
        label: 'Diesel Consumption Chart',
        icon: 'fas fa-gas-pump',
        description: 'Generator fuel burn over time',
        component: ChartWidget,
        variantFamily: 'chart',
        defaultVariant: 'bar-horizontal',
        defaultTitle: 'Diesel Consumption',
        defaultLayout: { w: 6, h: 4, minW: 4, minH: 3 },
        defaultSettings: { color: '#F59E0B' },
        defaultDataSource: { endpoint: '' },
    },
    'battery-chart': {
        type: 'battery-chart',
        label: 'Battery Charge/Discharge Chart',
        icon: 'fas fa-battery-full',
        description: 'Battery bank charge cycle over time',
        component: ChartWidget,
        variantFamily: 'chart',
        defaultVariant: 'polarArea',
        defaultTitle: 'Battery Charge / Discharge',
        defaultLayout: { w: 6, h: 4, minW: 4, minH: 3 },
        defaultSettings: { color: '#22C55E' },
        defaultDataSource: { endpoint: '' },
    },
    'emissions-chart': {
        type: 'emissions-chart',
        label: 'Carbon Emissions Chart',
        icon: 'fas fa-leaf',
        description: 'CO₂e emissions over time',
        component: ChartWidget,
        variantFamily: 'chart',
        defaultVariant: 'pie',
        defaultTitle: 'Carbon Emissions',
        defaultLayout: { w: 6, h: 4, minW: 4, minH: 3 },
        defaultSettings: { color: '#16A34A' },
        defaultDataSource: { endpoint: '' },
    },
    'water-chart': {
        type: 'water-chart',
        label: 'Water Usage Chart',
        icon: 'fas fa-droplet',
        description: 'Site water consumption over time',
        component: ChartWidget,
        variantFamily: 'chart',
        defaultVariant: 'donut',
        defaultTitle: 'Water Usage',
        defaultLayout: { w: 6, h: 4, minW: 4, minH: 3 },
        defaultSettings: { color: '#0EA5E9' },
        defaultDataSource: { endpoint: '' },
    },
    'uptime-chart': {
        type: 'uptime-chart',
        label: 'Equipment Uptime Chart',
        icon: 'fas fa-power-off',
        description: 'Uptime vs. downtime split',
        component: ChartWidget,
        variantFamily: 'chart',
        defaultVariant: 'scatter',
        defaultTitle: 'Equipment Uptime',
        defaultLayout: { w: 6, h: 4, minW: 4, minH: 3 },
        defaultSettings: { color: '#14B8A6' },
        defaultDataSource: { endpoint: '' },
    },
    'actual-vs-target-chart': {
        type: 'actual-vs-target-chart',
        label: 'Actual vs Target Chart',
        icon: 'fas fa-bullseye',
        description: 'Several series combined in one chart — actual, target & prior period',
        component: ChartWidget,
        variantFamily: 'chart',
        defaultVariant: 'combo',
        defaultTitle: 'Energy: Actual vs Target',
        defaultLayout: { w: 6, h: 4, minW: 4, minH: 3 },
        defaultSettings: { color: '#4318FF' },
        defaultDataSource: { endpoint: '' },
    },
    metric: {
        type: 'metric',
        label: 'Metric Card',
        icon: 'fas fa-tachometer-alt',
        description: "Single KPI value, e.g. Today's kWh (10 styles)",
        component: MetricWidget,
        variantFamily: 'metric',
        defaultVariant: 'big-number',
        defaultTitle: "Today's Energy",
        defaultLayout: { w: 2, h: 3, minW: 2, minH: 2 },
        defaultSettings: { unit: 'kWh', value: 0, precision: 1, color: '#4318FF' },
        defaultDataSource: { endpoint: '', field: 'value', staticValue: 0 },
    },
    table: {
        type: 'table',
        label: 'Meter Readings Table',
        icon: 'fas fa-table',
        description: 'Tabular list, e.g. meter readings (10 styles)',
        component: TableWidget,
        variantFamily: 'table',
        defaultVariant: 'basic',
        defaultTitle: 'Meter Readings',
        defaultLayout: { w: 4, h: 4, minW: 3, minH: 3 },
        defaultSettings: { color: '#4318FF' },
        defaultDataSource: { endpoint: '' },
    },
    'alarm-log-table': {
        type: 'alarm-log-table',
        label: 'Alarm Log Table',
        icon: 'fas fa-triangle-exclamation',
        description: 'Recent alarms & events (10 styles)',
        component: TableWidget,
        variantFamily: 'table',
        defaultVariant: 'status-badges',
        defaultTitle: 'Alarm Log',
        defaultLayout: { w: 4, h: 4, minW: 3, minH: 3 },
        defaultSettings: { color: '#ef4444' },
        defaultDataSource: {
            columns: ['Time', 'Meter', 'Event', 'Status'],
            rows: [
                ['09:41', 'GT2_EMS2', 'Voltage sag', 'Warning'],
                ['08:12', 'GT1_EMS1', 'Breaker trip', 'Critical'],
                ['Yesterday', 'GT3_EMS3', 'Comm loss', 'Warning'],
                ['Yesterday', 'GT1_EMS1', 'Restored', 'Active'],
            ],
        },
    },
    'billing-table': {
        type: 'billing-table',
        label: 'Billing Table',
        icon: 'fas fa-file-invoice-dollar',
        description: 'Monthly consumption & cost (10 styles)',
        component: TableWidget,
        variantFamily: 'table',
        defaultVariant: 'numbered',
        defaultTitle: 'Monthly Billing',
        defaultLayout: { w: 4, h: 4, minW: 3, minH: 3 },
        defaultSettings: { color: '#22C55E' },
        defaultDataSource: {
            columns: ['Month', 'kWh', 'Rate', 'Amount'],
            rows: [
                ['Jan', 12480, '₹7.2', '₹89,856'],
                ['Feb', 11820, '₹7.2', '₹85,104'],
                ['Mar', 13210, '₹7.4', '₹97,754'],
                ['Apr', 12990, '₹7.4', '₹96,126'],
            ],
        },
    },
    'equipment-table': {
        type: 'equipment-table',
        label: 'Equipment Inventory Table',
        icon: 'fas fa-boxes-stacked',
        description: 'Site asset register (10 styles)',
        component: TableWidget,
        variantFamily: 'table',
        defaultVariant: 'striped',
        defaultTitle: 'Equipment Inventory',
        defaultLayout: { w: 4, h: 4, minW: 3, minH: 3 },
        defaultSettings: { color: '#4318FF' },
        defaultDataSource: {
            columns: ['Asset ID', 'Type', 'Location', 'Status'],
            rows: [
                ['AST-1042', 'Transformer', 'Substation A', 'Active'],
                ['AST-1055', 'Generator', 'Plant Room', 'Active'],
                ['AST-1098', 'Chiller', 'Roof', 'Warning'],
                ['AST-1102', 'UPS', 'Server Room', 'Inactive'],
            ],
        },
    },
    'maintenance-table': {
        type: 'maintenance-table',
        label: 'Maintenance Log Table',
        icon: 'fas fa-screwdriver-wrench',
        description: 'Completed & scheduled maintenance (10 styles)',
        component: TableWidget,
        variantFamily: 'table',
        defaultVariant: 'dark-header',
        defaultTitle: 'Maintenance Log',
        defaultLayout: { w: 4, h: 4, minW: 3, minH: 3 },
        defaultSettings: { color: '#F59E0B' },
        defaultDataSource: {
            columns: ['Date', 'Equipment', 'Task', 'Technician'],
            rows: [
                ['Jul 12', 'AHU-2', 'Filter replacement', 'R. Menon'],
                ['Jul 15', 'FM-14', 'Calibration', 'S. Iyer'],
                ['Jul 18', 'T1', 'Oil inspection', 'R. Menon'],
            ],
        },
    },
    'access-log-table': {
        type: 'access-log-table',
        label: 'User Access Log Table',
        icon: 'fas fa-user-lock',
        description: 'Recent dashboard sign-ins (10 styles)',
        component: TableWidget,
        variantFamily: 'table',
        defaultVariant: 'compact',
        defaultTitle: 'User Access Log',
        defaultLayout: { w: 4, h: 4, minW: 3, minH: 3 },
        defaultSettings: { color: '#6b7280' },
        defaultDataSource: {
            columns: ['Time', 'User', 'Action', 'IP'],
            rows: [
                ['09:41', 'k.arthik', 'Login', '10.0.4.12'],
                ['09:12', 'a.sharma', 'Settings changed', '10.0.4.31'],
                ['Yesterday', 'k.arthik', 'Login', '10.0.4.12'],
            ],
        },
    },
    'status-list': {
        type: 'status-list',
        label: 'Meter Status List',
        icon: 'fas fa-list-check',
        description: 'Active/inactive status per meter (10 styles)',
        component: StatusListWidget,
        variantFamily: 'status-list',
        defaultVariant: 'dot-list',
        defaultTitle: 'Meter Status',
        defaultLayout: { w: 4, h: 4, minW: 3, minH: 3 },
        defaultSettings: {},
        defaultDataSource: { endpoint: '' },
    },
    progress: {
        type: 'progress',
        label: 'Circular Progress',
        icon: 'fas fa-circle-notch',
        description: 'Ring gauge, e.g. load % or target % (10 styles)',
        component: ProgressWidget,
        variantFamily: 'progress',
        defaultVariant: 'ring',
        defaultTitle: 'Load %',
        defaultLayout: { w: 2, h: 3, minW: 2, minH: 3 },
        defaultSettings: { value: 0, max: 100, color: '#4318FF', label: 'of capacity' },
        defaultDataSource: { endpoint: '' },
    },
    'more-widgets': {
        type: 'more-widgets',
        label: 'More Widgets',
        icon: 'fas fa-cubes',
        description: 'Sparkline, comparison, clock, notes, alerts, KPI grid, energy flow, and 3D visuals (26 styles)',
        component: ExtraWidget,
        variantFamily: 'extra',
        defaultVariant: 'sparkline',
        defaultTitle: 'New Widget',
        defaultLayout: { w: 3, h: 3, minW: 2, minH: 2 },
        defaultSettings: {
            unit: 'kWh', color: '#4318FF', value: 0, max: 100,
            currentLabel: 'Today', previousLabel: 'Yesterday', text: '',
        },
        defaultDataSource: {},
    },
};

export const widgetTypes = Object.values(widgetRegistry);
export default widgetRegistry;
